/**
 * Construction Builder Component
 *
 * Visual editor for building construction assemblies by layering materials.
 * Calculates and displays thermal properties.
 */

import { useCallback, useMemo } from 'react';
import { useGeometryEditStore, useConstructionBuilder } from '../../geometryEditStore';
import { useIDFStore } from '../../../stores/idfStore';
import type { MaterialLayer } from '../../editingTypes';
import { createConstructionIDFObject, updateConstructionIDFObject } from '../../sync/constructionSync';

/**
 * Props for ConstructionBuilder.
 */
interface ConstructionBuilderProps {
  onComplete?: () => void;
  onCancel?: () => void;
}

/**
 * Get color/pattern for material category.
 */
function getMaterialStyle(category: MaterialLayer['category']): string {
  switch (category) {
    case 'masonry':
      return 'bg-gradient-to-r from-gray-400 to-gray-500';
    case 'insulation':
      return 'bg-gradient-to-r from-yellow-200 to-yellow-300';
    case 'air-gap':
      return 'bg-gradient-to-r from-blue-100 to-blue-200 bg-stripes';
    case 'finish':
      return 'bg-gradient-to-r from-slate-200 to-slate-300';
    default:
      return 'bg-gradient-to-r from-gray-200 to-gray-300';
  }
}

/**
 * Construction Builder Component.
 */
export function ConstructionBuilder({ onComplete, onCancel }: ConstructionBuilderProps) {
  const builder = useConstructionBuilder();
  const closeConstructionBuilder = useGeometryEditStore((s) => s.closeConstructionBuilder);
  const updateEditingConstruction = useGeometryEditStore((s) => s.updateEditingConstruction);
  const addConstructionLayer = useGeometryEditStore((s) => s.addConstructionLayer);
  const removeConstructionLayer = useGeometryEditStore((s) => s.removeConstructionLayer);
  const moveConstructionLayer = useGeometryEditStore((s) => s.moveConstructionLayer);
  const recordAction = useGeometryEditStore((s) => s.recordAction);
  const addNotification = useGeometryEditStore((s) => s.addNotification);

  const document = useIDFStore((s) => s.document);
  const addObject = useIDFStore((s) => s.addObject);
  const updateObject = useIDFStore((s) => s.updateObject);
  const getObjectById = useIDFStore((s) => s.getObjectById);

  // Get available materials from IDF
  const materialOptions = useMemo(() => {
    if (!document) return [];

    const materials: Array<{ name: string; type: string; thickness?: number }> = [];

    for (const obj of document.objects) {
      const className = obj.className.toLowerCase();
      const name = obj.fields[0]?.value;

      if (!name) continue;

      if (className === 'material') {
        const thickness = parseFloat(obj.fields[2]?.value ?? '0');
        materials.push({ name, type: 'Material', thickness });
      } else if (className === 'material:airgap') {
        materials.push({ name, type: 'Air Gap', thickness: 0.025 });
      } else if (className === 'material:nomass') {
        materials.push({ name, type: 'No Mass' });
      }
    }

    return materials;
  }, [document]);

  const handleClose = useCallback(() => {
    closeConstructionBuilder();
    onCancel?.();
  }, [closeConstructionBuilder, onCancel]);

  const handleSave = useCallback(() => {
    if (!builder.editingConstruction) return;

    if (!builder.editingConstruction.name.trim()) {
      addNotification('error', 'Please enter a construction name');
      return;
    }

    if (builder.editingConstruction.layers.length === 0) {
      addNotification('error', 'Please add at least one layer');
      return;
    }

    // Create IDF object
    const layerNames = builder.editingConstruction.layers.map((l) => l.materialName);
    const result = createConstructionIDFObject(builder.editingConstruction.name, layerNames);

    if (builder.isNew) {
      // Add new construction
      const constructionId = addObject(result.construction);

      recordAction({
        type: 'construction-create',
        description: `Create construction "${builder.editingConstruction.name}"`,
        data: { construction: builder.editingConstruction },
        inverseData: { constructionId },
        createdIds: [constructionId],
        deletedIds: [],
      });

      addNotification('success', `Construction "${builder.editingConstruction.name}" created`);
    } else if (builder.editingConstruction.sourceId) {
      // Update existing construction
      const existingObj = getObjectById(builder.editingConstruction.sourceId);
      if (existingObj) {
        const updated = updateConstructionIDFObject(existingObj, {
          name: builder.editingConstruction.name,
          layerNames,
        });
        updateObject(builder.editingConstruction.sourceId, { fields: updated.fields });

        recordAction({
          type: 'construction-update',
          description: `Update construction "${builder.editingConstruction.name}"`,
          data: { construction: builder.editingConstruction },
          inverseData: { construction: builder.editingConstruction },
          createdIds: [],
          deletedIds: [],
        });

        addNotification('success', `Construction "${builder.editingConstruction.name}" updated`);
      }
    }

    closeConstructionBuilder();
    onComplete?.();
  }, [
    builder,
    addObject,
    updateObject,
    getObjectById,
    recordAction,
    addNotification,
    closeConstructionBuilder,
    onComplete,
  ]);

  const handleAddLayer = useCallback(
    (materialName: string) => {
      const material = materialOptions.find((m) => m.name === materialName);
      if (!material) return;

      let category: MaterialLayer['category'] = 'other';
      if (material.type === 'Air Gap') {
        category = 'air-gap';
      }

      addConstructionLayer({
        materialName,
        thickness: material.thickness ?? 0.1,
        category,
      });
    },
    [materialOptions, addConstructionLayer]
  );

  if (!builder.isOpen || !builder.editingConstruction) return null;

  const construction = builder.editingConstruction;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/70"
      role="dialog"
      aria-modal="true"
      aria-labelledby="construction-builder-title"
    >
      <div className="w-full max-w-2xl rounded-lg border border-border bg-card shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 id="construction-builder-title" className="text-lg font-semibold text-foreground">
            {builder.isNew ? 'Create Construction' : 'Edit Construction'}
          </h2>
          <button
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background rounded"
            aria-label="Close dialog"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {/* Name */}
          <div className="mb-4">
            <label htmlFor="construction-name" className="mb-1 block text-sm font-medium text-foreground">
              Construction Name
            </label>
            <input
              type="text"
              id="construction-name"
              value={construction.name}
              onChange={(e) => updateEditingConstruction({ name: e.target.value })}
              className="w-full rounded border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="Enter name..."
            />
          </div>

          {/* Layers */}
          <div className="mb-4">
            <div className="mb-2 flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">
                Layers (Outside to Inside)
              </label>
              <div className="flex items-center gap-2">
                {materialOptions.length === 0 ? (
                  <span className="text-xs text-muted-foreground">No materials in IDF</span>
                ) : (
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        handleAddLayer(e.target.value);
                        e.target.value = '';
                      }
                    }}
                    className="rounded border border-border bg-background px-2 py-1 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    defaultValue=""
                    aria-label="Add material layer"
                  >
                    <option value="" disabled>
                      + Add Layer
                    </option>
                    {materialOptions.map((m) => (
                      <option key={m.name} value={m.name}>
                        {m.name} ({m.type})
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            {/* Layer list */}
            <div className="rounded-lg border border-border bg-muted/50">
              {construction.layers.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">
                  No layers added. Select a material from the dropdown above.
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {construction.layers.map((layer, index) => (
                    <div
                      key={layer.id}
                      className="flex items-center gap-3 px-4 py-2"
                    >
                      {/* Material visualization */}
                      <div
                        className={`h-8 rounded ${getMaterialStyle(layer.category)}`}
                        style={{ width: Math.max(20, Math.min(80, layer.thickness * 200)) }}
                        aria-hidden="true"
                      />

                      {/* Material info */}
                      <div className="flex-1">
                        <div className="text-sm font-medium text-foreground">
                          {layer.materialName}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {layer.thickness > 0
                            ? `${(layer.thickness * 1000).toFixed(0)} mm`
                            : 'No thickness'}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1" role="group" aria-label="Layer actions">
                        <button
                          onClick={() => moveConstructionLayer(layer.id, 'up')}
                          disabled={index === 0}
                          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
                          title="Move up (toward outside)"
                          aria-label="Move layer up"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => moveConstructionLayer(layer.id, 'down')}
                          disabled={index === construction.layers.length - 1}
                          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
                          title="Move down (toward inside)"
                          aria-label="Move layer down"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => removeConstructionLayer(layer.id)}
                          className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive focus:outline-none focus:ring-2 focus:ring-destructive focus:ring-offset-1"
                          title="Remove layer"
                          aria-label="Remove layer"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Properties */}
          <div className="rounded-lg border border-border bg-muted/50 p-4">
            <h3 className="mb-3 text-sm font-medium text-foreground">Properties</h3>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground">Total Thickness</div>
                <div className="text-lg font-semibold text-foreground">
                  {(construction.totalThickness * 1000).toFixed(0)} mm
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">U-Value</div>
                <div className="text-lg font-semibold text-foreground">
                  {construction.uValue.toFixed(2)} W/m²·K
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">R-Value</div>
                <div className="text-lg font-semibold text-foreground">
                  {construction.rValue.toFixed(2)} m²·K/W
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Layers</div>
                <div className="text-lg font-semibold text-foreground">
                  {construction.layers.length}
                </div>
              </div>
            </div>
          </div>

          {/* Visual cross-section */}
          {construction.layers.length > 0 && (
            <div className="mt-4">
              <h3 className="mb-2 text-sm font-medium text-foreground">Cross-Section View</h3>
              <div className="flex h-24 items-center rounded-lg border border-border bg-card p-2">
                <div className="mr-2 text-xs text-muted-foreground">OUT</div>
                {construction.layers.map((layer) => (
                  <div
                    key={layer.id}
                    className={`h-full ${getMaterialStyle(layer.category)} border-r border-border`}
                    style={{
                      width: Math.max(
                        10,
                        Math.min(100, (layer.thickness / construction.totalThickness) * 300)
                      ),
                    }}
                    title={`${layer.materialName} (${(layer.thickness * 1000).toFixed(0)}mm)`}
                  />
                ))}
                <div className="ml-2 text-xs text-muted-foreground">IN</div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-border bg-muted/50 px-6 py-4">
          <button
            onClick={handleClose}
            className="rounded border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!construction.name.trim() || construction.layers.length === 0}
            className="rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background"
          >
            {builder.isNew ? 'Create Construction' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConstructionBuilder;
